import { TabType } from '@/components/DetailTabs';
import {
  AccountParams,
  AssetIdParams,
  ColumnNameParams,
  DatabaseNameParams,
  EnvironmentIdParams,
  ExposureTileParams,
  LinkGenerator,
  OriginColumnParams,
  OriginProjectParams,
  PackageNameParams,
  RecommendationFilters,
  ResourceFilterTypeParams,
  SchemaNameParams,
  SearchParams,
  SelectColumnParams,
  SelectorParams,
  SourceNameParams,
  TabParams,
  UniqueIdParams,
  UrlProjectParams,
  ViewParams,
} from './linkTypes';

/**
 * Each function within this file accepts LinkGeneratorParams (or an extension of it) and returns
 * a generated path that you can link to.
 *
 * Note: Adding and exporting a function from this file will list it as a `Linkable` for `useLinkTo`
 * Note 2: camelCase names of Linkables here, even if they include a `-`
 */

/**
 * MultiProject Linkables
 */
export const multiProject: LinkGenerator<
  AccountParams &
    Partial<ViewParams> &
    Partial<TabParams> &
    Partial<SearchParams> &
    Partial<{ fragment: string }>
> = (params) => {
  const projectScope = params;
  const searchParams = new URLSearchParams();
  if (params.view) {
    searchParams.set('view', params.view);
  }
  if (params.tab) {
    searchParams.set('tab', params.tab);
  }
  if (params.search) {
    searchParams.set('search', params.search);
  }
  const paramString = searchParams.toString();
  const fragmentString = params.fragment ? `#${params.fragment}` : '';
  return `/${projectScope.accountId}/${paramString ? `?${paramString}` : ''}${fragmentString}`;
};

export const project: LinkGenerator<
  AccountParams & UrlProjectParams & Partial<OriginProjectParams>
> = (params) => {
  const searchParams = new URLSearchParams();
  if (params.originProject) {
    searchParams.set('originProject', params.originProject.toString());
  }
  if (searchParams.toString()) {
    return `/${params.accountId}/projects/${params.projectId}/environments/${params.environmentType}/?${searchParams.toString()}`;
  }
  return `/${params.accountId}/projects/${params.projectId}/environments/${params.environmentType}/`;
};

export const projectLineage: LinkGenerator<
  AccountParams &
    UrlProjectParams &
    Partial<UniqueIdParams> &
    Partial<EnvironmentIdParams> &
    Partial<SelectorParams> &
    Partial<OriginProjectParams>
> = (params) => {
  const searchParams = new URLSearchParams();
  if (params.dbtUniqueId) {
    searchParams.set('uniqueId', params.dbtUniqueId);
  }
  if (params.selector) {
    searchParams.set('select', params.selector);
  }
  if (params.environmentId) {
    searchParams.set('environmentId', params.environmentId.toString());
  }
  if (params.originProject) {
    searchParams.set('originProject', params.originProject.toString());
  }
  const paramString = searchParams.toString();

  // We need to exclude originProject from the projectParams object to avoid it being added to the URL twice
  const { originProject: _originProject, ...projectParams } = params;
  if (paramString) {
    return `${project(projectParams)}lineage/?${paramString}`;
  }
  return `${project(projectParams)}lineage/`;
};

export const columnLineage: LinkGenerator<
  AccountParams &
    UrlProjectParams &
    Partial<UniqueIdParams> &
    Partial<ColumnNameParams> &
    Partial<SelectColumnParams>
> = (params) => {
  const searchParams = new URLSearchParams();
  if (params.dbtUniqueId) {
    searchParams.set('uniqueId', params.dbtUniqueId);
    searchParams.set('columnName', params.columnName || '');
  }
  if (params.selectedColumn) {
    searchParams.set('selectedColumn', params.selectedColumn);
  }
  const paramString = searchParams.toString();
  if (paramString) {
    return `${project(params)}columnlineage/?${paramString}`;
  }
  return `${project(params)}columnlineage/`;
};

export const searchResults: LinkGenerator<
  AccountParams & UrlProjectParams & SearchParams
> = (params) => {
  const searchParams = new URLSearchParams();
  if (params.filters) searchParams.set('filters', params.filters);
  if (params.search) searchParams.set('search', params.search);

  const paramString = searchParams.toString();
  if (paramString) {
    return `${project(params)}search/?${paramString}`;
  }
  return `${project(params)}search/`;
};

export const summitSearchResults: LinkGenerator<AccountParams & SearchParams> = (
  params,
) => {
  const searchParams = new URLSearchParams();
  if (params.filters) searchParams.set('filters', params.filters);
  if (params.search) searchParams.set('search', params.search);

  const paramString = searchParams.toString();
  if (paramString) {
    return `/${params.accountId}/search/?${paramString}`;
  }
  return `/${params.accountId}/search/`;
};

export const resourceFilter: LinkGenerator<
  AccountParams & UrlProjectParams & ResourceFilterTypeParams
> = (params) => {
  let searchParams: string = '';
  if (params.resourceType === 'model') {
    const paramsMap: Record<string, any> = {};
    if ('access' in params && params.access) {
      paramsMap.access = params.access;
    }
    if ('modelingLayer' in params && params.modelingLayer) {
      paramsMap.modelingLayer = params.modelingLayer;
    }
    if ('lastRunStatus' in params && params.lastRunStatus) {
      paramsMap.lastRunStatus = params.lastRunStatus;
    }
    if ('sort' in params && params.sort) {
      paramsMap.sortDir = params.sort.direction;
      paramsMap.sortField = params.sort.field;
    }
    searchParams = new URLSearchParams(paramsMap).toString();
  }

  return `${project(params)}resource/${params.resourceType}/${searchParams ? `?${searchParams}` : ''}`;
};

export const resourceDetails: LinkGenerator<
  AccountParams &
    UrlProjectParams &
    UniqueIdParams &
    Partial<TabParams<TabType>> &
    Partial<OriginColumnParams> &
    Partial<OriginProjectParams>
> = (params) => {
  const searchParams = new URLSearchParams();
  if (params.tab) {
    searchParams.set('tab', params.tab);
  }
  if (params.originColumn) {
    searchParams.set('originColumn', params.originColumn);
  }
  if (params.originProject) {
    searchParams.set('originProject', params.originProject.toString());
  }
  const paramString = searchParams.toString();
  const { originProject: _originProject, ...projectParams } = params;
  if (paramString) {
    return `${project(projectParams)}details/${params.dbtUniqueId}/?${paramString}`;
  }
  return `${project(projectParams)}details/${params.dbtUniqueId}`;
};

export const catalogAsset: LinkGenerator<
  AccountParams & AssetIdParams & Partial<TabParams<TabType>>
> = (params) => {
  const searchParams = new URLSearchParams();
  if (params.tab) {
    searchParams.set('tab', params.tab);
  }
  const paramString = searchParams.toString();

  const urlAssetId = ['Snowflake'].includes(params.assetId) // TODO: add bigquery, redshift, databricks eventually
    ? params.assetId.toLowerCase()
    : params.assetId;
  return `/${params.accountId}/catalog/${urlAssetId}${paramString ? `?${paramString}` : ''}`;
};

export const sourceCollectionFilter: LinkGenerator<
  AccountParams & UrlProjectParams & SourceNameParams
> = (params) => {
  return `${project(params)}resource/source/${params.sourceName}/`;
};

export const packageDetails: LinkGenerator<
  AccountParams & UrlProjectParams & PackageNameParams
> = (params) => {
  return `${project(params)}package/${params.packageName}/`;
};

export const schemaDetails: LinkGenerator<
  AccountParams & UrlProjectParams & DatabaseNameParams & SchemaNameParams
> = (params) => {
  return `${project(params)}schema/${params.databaseName}/${params.schemaName}/`;
};

export const performance: LinkGenerator<AccountParams & UrlProjectParams> = (
  params,
) => {
  return `${project(params)}performance/`;
};

export const recommendations: LinkGenerator<
  AccountParams & UrlProjectParams & RecommendationFilters
> = (props) => {
  const { accountId, projectId, environmentType: environmentName, ...rest } = props;
  const queryString = Object.entries(rest)
    .map(([key, value]) => `${key}=${value}`)
    .join('&');
  if (!queryString) {
    return `${project({ accountId, projectId, environmentType: environmentName })}recommendations/`;
  }
  return `${project({ accountId, projectId, environmentType: environmentName })}recommendations/?${queryString}`;
};

export const exposureTile: LinkGenerator<ExposureTileParams> = (params) => {
  let url =
    `${params.metadataUri}/exposure-tile?uniqueId=${params.uniqueId}&environmentType=${params.environmentType}&environmentId=${params.environmentId}&token=${params.token}`.replaceAll(
      '//exposure',
      '/exposure',
    );
  if (params.biResourceId) {
    url += `&biResourceId=${params.biResourceId}`;
  }
  if (params.autoBiProvider) {
    url += `&autoBiProvider=${params.autoBiProvider}`;
  }
  return url;
};
